/**
 * Reusable overlay layer hooks for any Nowcasting map instance.
 *
 * - useCircleOverlayLayer: 3 km circuit ring
 * - useRadarBboxOverlayLayer: radar bounding box rectangle
 * - useRadiusCirclesLayer: configurable distance rings
 * - useLayerOrder: enforces z-order on idle
 */
import { useEffect, useCallback } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import type { EventWithCircuit } from '../../api/eventsService';
import type { NowcastingMapIds } from './nowcastingMapIds';

const MAX_RETRIES = 20;
const RETRY_DELAY = 50;

// ============================================================================
// Circle overlay (3 km ring)
// ============================================================================

export interface UseCircleOverlayLayerParams {
  mapRef: React.RefObject<MapboxMap | null>;
  enabled: boolean;
  selectedEvent: EventWithCircuit | null;
  isVisible: boolean;
  styleVersion: number;
  sourceId: string;
  layerId: string;
}

export function useCircleOverlayLayer({
  mapRef,
  enabled,
  selectedEvent,
  isVisible,
  styleVersion,
  sourceId,
  layerId,
}: UseCircleOverlayLayerParams): void {
  const createCircleGeoJSON = useCallback((centerLon: number, centerLat: number, radiusKm: number, points: number = 64): GeoJSON.Feature<GeoJSON.Polygon> => {
    const coords: [number, number][] = [];
    for (let i = 0; i <= points; i++) {
      const angle = (i / points) * 2 * Math.PI;
      const latOffset = (radiusKm / 111.32) * Math.cos(angle);
      const lonOffset = (radiusKm / (111.32 * Math.cos(centerLat * Math.PI / 180))) * Math.sin(angle);
      coords.push([centerLon + lonOffset, centerLat + latOffset]);
    }
    return {
      type: 'Feature',
      properties: {},
      geometry: { type: 'Polygon', coordinates: [coords] },
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedEvent || !enabled) return;

    let retryCount = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const addCircleLayer = () => {
      if (!map.isStyleLoaded()) {
        retryCount++;
        if (retryCount < MAX_RETRIES) {
          timeoutId = setTimeout(addCircleLayer, RETRY_DELAY);
        }
        return;
      }
      try {
        if (!map.getSource(sourceId)) {
          const circuitLat = selectedEvent.circuit.latitude || 0;
          const circuitLon = selectedEvent.circuit.longitude || 0;
          const circleGeoJSON = createCircleGeoJSON(circuitLon, circuitLat, 3);

          map.addSource(sourceId, { type: 'geojson', data: circleGeoJSON });
          map.addLayer({
            id: layerId,
            type: 'line',
            source: sourceId,
            paint: {
              'line-color': '#ff4d4f',
              'line-width': ['interpolate', ['linear'], ['zoom'], 5, 8, 10, 5, 13, 3, 16, 2],
              'line-opacity': 0.9,
            },
          });
        }
        if (map.getLayer(layerId)) {
          map.moveLayer(layerId);
        }
      } catch {
        // Ignore errors during layer creation
      }
    };

    const removeCircleLayer = () => {
      try {
        if (!map.getStyle()) return;
        if (map.getLayer(layerId)) map.removeLayer(layerId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      } catch {
        // Ignore errors during cleanup
      }
    };

    const moveCircleToTop = () => {
      try {
        if (map.getStyle() && map.getLayer(layerId)) map.moveLayer(layerId);
      } catch { /* ignore */ }
    };

    if (isVisible) {
      addCircleLayer();
      map.on('idle', moveCircleToTop);
    } else {
      if (map.isStyleLoaded()) removeCircleLayer();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      map.off('idle', moveCircleToTop);
      removeCircleLayer();
    };
  }, [isVisible, selectedEvent, createCircleGeoJSON, mapRef, styleVersion, enabled, sourceId, layerId]);
}

// ============================================================================
// Radar bounding box overlay
// ============================================================================

export interface UseRadarBboxOverlayLayerParams {
  mapRef: React.RefObject<MapboxMap | null>;
  enabled: boolean;
  selectedEvent: EventWithCircuit | null;
  isVisible: boolean;
  styleVersion: number;
  sourceId: string;
  layerId: string;
}

export function useRadarBboxOverlayLayer({
  mapRef,
  enabled,
  selectedEvent,
  isVisible,
  styleVersion,
  sourceId,
  layerId,
}: UseRadarBboxOverlayLayerParams): void {
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedEvent || !enabled) return;

    let retryCount = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const getCoordinates = (): [[number, number], [number, number], [number, number], [number, number]] | null => {
      if (selectedEvent.radar_bbox && selectedEvent.radar_bbox !== null && selectedEvent.radar_bbox.type === 'Polygon') {
        const polygonCoords = selectedEvent.radar_bbox.coordinates[0];
        let west = Infinity, east = -Infinity, south = Infinity, north = -Infinity;
        polygonCoords.forEach((coord: number[]) => {
          west = Math.min(west, coord[0]);
          east = Math.max(east, coord[0]);
          south = Math.min(south, coord[1]);
          north = Math.max(north, coord[1]);
        });
        return [[west, north], [east, north], [east, south], [west, south]];
      }
      const circuitLat = selectedEvent.circuit.latitude || 0;
      const circuitLon = selectedEvent.circuit.longitude || 0;
      const latOffset = 0.45;
      const lonOffset = 0.45;
      return [
        [circuitLon - lonOffset, circuitLat + latOffset],
        [circuitLon + lonOffset, circuitLat + latOffset],
        [circuitLon + lonOffset, circuitLat - latOffset],
        [circuitLon - lonOffset, circuitLat - latOffset],
      ];
    };

    const addRadarBboxLayer = () => {
      if (!map.isStyleLoaded()) {
        retryCount++;
        if (retryCount < MAX_RETRIES) {
          timeoutId = setTimeout(addRadarBboxLayer, RETRY_DELAY);
        }
        return;
      }
      try {
        if (!map.getSource(sourceId)) {
          const coords = getCoordinates();
          if (!coords) return;
          map.addSource(sourceId, {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: { type: 'Polygon', coordinates: [[...coords, coords[0]]] },
            },
          });
          map.addLayer({
            id: layerId,
            type: 'line',
            source: sourceId,
            paint: {
              'line-color': '#1890ff',
              'line-width': ['interpolate', ['linear'], ['zoom'], 5, 6, 10, 4, 13, 3, 16, 2],
              'line-opacity': 0.9,
              'line-dasharray': [2, 2],
            },
          });
        }
        if (map.getLayer(layerId)) map.moveLayer(layerId);
      } catch {
        // Ignore errors
      }
    };

    const removeRadarBboxLayer = () => {
      try {
        if (!map.getStyle()) return;
        if (map.getLayer(layerId)) map.removeLayer(layerId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      } catch { /* ignore */ }
    };

    const moveToTop = () => {
      try {
        if (map.getStyle() && map.getLayer(layerId)) map.moveLayer(layerId);
      } catch { /* ignore */ }
    };

    if (isVisible) {
      addRadarBboxLayer();
      map.on('idle', moveToTop);
    } else {
      if (map.isStyleLoaded()) removeRadarBboxLayer();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      map.off('idle', moveToTop);
      removeRadarBboxLayer();
    };
  }, [isVisible, selectedEvent, mapRef, styleVersion, enabled, sourceId, layerId]);
}

// ============================================================================
// Radius circles (distance rings)
// ============================================================================

export interface UseRadiusCirclesLayerParams {
  mapRef: React.RefObject<MapboxMap | null>;
  enabled: boolean;
  selectedEvent: EventWithCircuit | null;
  isVisible: boolean;
  circleCount: number;
  circleDistance: number;
  styleVersion: number;
  circleSourcePrefix: string;
  circleLayerPrefix: string;
  labelSourcePrefix: string;
  labelLayerPrefix: string;
}

export function useRadiusCirclesLayer({
  mapRef,
  enabled,
  selectedEvent,
  isVisible,
  circleCount,
  circleDistance,
  styleVersion,
  circleSourcePrefix,
  circleLayerPrefix,
  labelSourcePrefix,
  labelLayerPrefix,
}: UseRadiusCirclesLayerParams): void {
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedEvent || !enabled) return;

    let retryCount = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const addRadiusCircles = () => {
      if (!map.isStyleLoaded()) {
        retryCount++;
        if (retryCount < MAX_RETRIES) {
          timeoutId = setTimeout(addRadiusCircles, RETRY_DELAY);
        }
        return;
      }
      try {
        const circuitLat = selectedEvent.circuit.latitude || 0;
        const circuitLon = selectedEvent.circuit.longitude || 0;

        for (let i = 1; i <= circleCount; i++) {
          const radiusKm = i * circleDistance;
          const sid = `${circleSourcePrefix}${i}`;
          const lid = `${circleLayerPrefix}${i}`;
          const lsid = `${labelSourcePrefix}${i}`;
          const llid = `${labelLayerPrefix}${i}`;

          if (map.getLayer(llid)) map.removeLayer(llid);
          if (map.getSource(lsid)) map.removeSource(lsid);
          if (map.getLayer(lid)) map.removeLayer(lid);
          if (map.getSource(sid)) map.removeSource(sid);

          const points = 64;
          const coordinates: [number, number][] = [];
          for (let j = 0; j <= points; j++) {
            const angle = (j / points) * 2 * Math.PI;
            const latOffset = (radiusKm / 111.32) * Math.cos(angle);
            const lonOffset = (radiusKm / (111.32 * Math.cos(circuitLat * Math.PI / 180))) * Math.sin(angle);
            coordinates.push([circuitLon + lonOffset, circuitLat + latOffset]);
          }

          map.addSource(sid, {
            type: 'geojson',
            data: { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [coordinates] } },
          });
          map.addLayer({
            id: lid,
            type: 'line',
            source: sid,
            paint: {
              'line-color': '#ff0000',
              'line-width': ['interpolate', ['linear'], ['zoom'], 5, 4, 10, 2.5, 13, 2, 16, 1.5],
              'line-opacity': 0.8,
              'line-dasharray': [4, 2],
            },
          });

          const labelLat = circuitLat + (radiusKm / 111.32);
          map.addSource(lsid, {
            type: 'geojson',
            data: { type: 'Feature', properties: { distance: `${radiusKm} km` }, geometry: { type: 'Point', coordinates: [circuitLon, labelLat] } },
          });
          map.addLayer({
            id: llid,
            type: 'symbol',
            source: lsid,
            layout: {
              'text-field': ['get', 'distance'],
              'text-size': ['interpolate', ['linear'], ['zoom'], 5, 10, 10, 12, 13, 14, 16, 16],
              'text-anchor': 'bottom',
              'text-offset': [0, -0.3],
              'text-allow-overlap': true,
            },
            paint: {
              'text-color': '#ff0000',
              'text-halo-color': '#ffffff',
              'text-halo-width': 2,
            },
          });
        }

        for (let i = 1; i <= circleCount; i++) {
          if (map.getLayer(`${circleLayerPrefix}${i}`)) map.moveLayer(`${circleLayerPrefix}${i}`);
          if (map.getLayer(`${labelLayerPrefix}${i}`)) map.moveLayer(`${labelLayerPrefix}${i}`);
        }
      } catch {
        // Ignore errors during layer creation
      }
    };

    const removeRadiusCircles = () => {
      try {
        if (!map.getStyle()) return;
        for (let i = 1; i <= 10; i++) {
          if (map.getLayer(`${labelLayerPrefix}${i}`)) map.removeLayer(`${labelLayerPrefix}${i}`);
          if (map.getSource(`${labelSourcePrefix}${i}`)) map.removeSource(`${labelSourcePrefix}${i}`);
          if (map.getLayer(`${circleLayerPrefix}${i}`)) map.removeLayer(`${circleLayerPrefix}${i}`);
          if (map.getSource(`${circleSourcePrefix}${i}`)) map.removeSource(`${circleSourcePrefix}${i}`);
        }
      } catch {
        // Ignore errors during cleanup
      }
    };

    const moveCirclesToTop = () => {
      try {
        if (!map.getStyle()) return;
        for (let i = 1; i <= circleCount; i++) {
          if (map.getLayer(`${circleLayerPrefix}${i}`)) map.moveLayer(`${circleLayerPrefix}${i}`);
          if (map.getLayer(`${labelLayerPrefix}${i}`)) map.moveLayer(`${labelLayerPrefix}${i}`);
        }
      } catch { /* ignore */ }
    };

    if (isVisible) {
      addRadiusCircles();
      map.on('idle', moveCirclesToTop);
    } else {
      if (map.isStyleLoaded()) removeRadiusCircles();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      map.off('idle', moveCirclesToTop);
      removeRadiusCircles();
    };
  }, [isVisible, circleCount, circleDistance, selectedEvent, mapRef, styleVersion, enabled, circleSourcePrefix, circleLayerPrefix, labelSourcePrefix, labelLayerPrefix]);
}

// ============================================================================
// Layer order enforcement
// ============================================================================

export interface UseLayerOrderParams {
  mapRef: React.RefObject<MapboxMap | null>;
  enabled: boolean;
  styleVersion: number;
  ids: NowcastingMapIds;
  extraLayers?: string[];
}

/**
 * Enforces z-order on idle: raster -> circuit -> arrows -> stations -> extra -> overlays.
 * `extraLayers` is for map-specific layers (rain-path, storm-tracking) inserted above stations.
 */
export function useLayerOrder({
  mapRef,
  enabled,
  styleVersion,
  ids,
  extraLayers = [],
}: UseLayerOrderParams): void {
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !enabled) return;

    const enforceLayerOrder = () => {
      try {
        if (!map.getStyle()) return;

        if (map.getLayer(ids.circuitLineLayer)) map.moveLayer(ids.circuitLineLayer);
        if (map.getLayer(ids.windArrowsLayer)) map.moveLayer(ids.windArrowsLayer);
        if (map.getLayer(ids.stationsLayer)) map.moveLayer(ids.stationsLayer);
        if (map.getLayer(ids.stationsLabelsLayer)) map.moveLayer(ids.stationsLabelsLayer);

        for (const layer of extraLayers) {
          if (map.getLayer(layer)) map.moveLayer(layer);
        }

        if (map.getLayer(ids.circleOverlayLayer)) map.moveLayer(ids.circleOverlayLayer);
        if (map.getLayer(ids.radarBboxLayer)) map.moveLayer(ids.radarBboxLayer);

        for (let i = 1; i <= 10; i++) {
          if (map.getLayer(`${ids.radiusCircleLayerPrefix}${i}`)) map.moveLayer(`${ids.radiusCircleLayerPrefix}${i}`);
          if (map.getLayer(`${ids.radiusLabelLayerPrefix}${i}`)) map.moveLayer(`${ids.radiusLabelLayerPrefix}${i}`);
        }
      } catch {
        // Ignore errors during layer reordering
      }
    };

    map.on('idle', enforceLayerOrder);
    return () => { map.off('idle', enforceLayerOrder); };
  }, [mapRef, styleVersion, enabled, ids, extraLayers]);
}
